import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  buildPatchDto,
  getObjectId, PatchDTO,
  Site,
  SITE_PATCH_FIELD_MAP,
  siteSelector,
  useGetSiteQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {SiteForm, SiteFormValues} from "../../components/form/SiteForm/SiteForm";
import {useSiteMutation} from "../../hooks/useSiteMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {v4 as uuid} from "uuid";
import {toFormDefaultsFromSite} from "../../components/form/SiteForm/useSiteForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<SiteFormValues>
}

export const SiteFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <SiteFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const SiteFormPage = (props: Props) => {
  const {id, copy = false, formDefaults} = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectSite = useMemo(() => {
    return siteSelector()
  }, [])

  const {currSite, isLoading} = useGetSiteQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currSite: selectSite(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<SiteFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromSite(currSite);
  }, [currSite, formDefaults]);

  const isEdit = useMemo(() => {
    return currSite && !copy
  }, [currSite, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.site.form.title.edit", "Edit Site", {ns: "common"});
    } else {
      return t("page.entity.site.form.title.new", "New Site", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createSite,
    patchUpdateSite,
    isCreating,
    isUpdating,
  } = useSiteMutation()

  const onSiteSubmit = useCallback((formValues: SiteFormValues) => {
    const patch: Partial<Site> = {
      name: formValues.name,
      leaseId: formValues.leaseId,
      leaseName: formValues.leaseName,
      type: formValues.type?.code,
      waterTypeId: getObjectId(formValues.freightType),
      companyId: getObjectId(formValues.operator),
      ownerId: getObjectId(formValues.owner),
      operatingAreaId: getObjectId(formValues.operatingArea),
      companyUnitId: getObjectId(formValues.companyUnit),
      parentSiteId: getObjectId(formValues.parentSite),
      geofenceSiteGroupId: getObjectId(formValues.geofenceSiteGroup),
      afe: formValues.afe,
      afePrefix: formValues?.afePrefix,
      afeSuffix: formValues?.afeSuffix,
      address: formValues?.address,
      latCoord: formValues.latCoord,
      longCoord: formValues.longCoord,
      transportViaPipeline: formValues?.transportViaPipeline,
      transportOilViaPipeline: formValues?.transportOilViaPipeline,
      doNotUse: formValues.doNotUse,
      regionList: regionId ? [regionId] : undefined,
    }

    if (isEdit) {
      const dto: PatchDTO<any> = buildPatchDto(patch, SITE_PATCH_FIELD_MAP)
      patchUpdateSite(currSite.id, dto, {onSuccess: () => navigate(-1) })
    } else {
      const site: Partial<Site> = {
        ...patch,
        id: uuid(), // TODO: remove once api returns site payload
      };
      createSite(site, {onSuccess: (payload) => navigate(`/sites/profile?id=${site.id}`, {replace: true})})
    }
  }, [regionId, currSite, isEdit, patchUpdateSite, navigate, createSite]);

  return (
    <ProtectedPage
      entity={currSite}
      permissions={[isEdit ? "site_edit" : "site_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <SiteForm
                  options={{
                    regionId: regionId,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onSiteSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

